import type { CSSProperties } from 'react';
import { useReducedMotion } from '../lib/useReducedMotion.js';

type Tone = 'subtle' | 'normal' | 'bright' | 'nova';

interface Spark {
  left: number;
  top: number;
  size: number;
  dur: number;
  delay: number;
  tone: Tone;
  point?: boolean;
  metallic?: boolean;
}

// Peak (max) opacity and glow blur per brightness level. Minimum opacity is a
// fraction of the peak (see CSS) so stars stay clearly visible throughout.
const PEAK: Record<Tone, number> = { subtle: 0.42, normal: 0.68, bright: 0.92, nova: 1 };
const BLUR: Record<Tone, number> = { subtle: 3, normal: 5, bright: 9, nova: 12 };

/** Curated animated 4-point sparkles — clustered near the branding, drifting. */
const STARS: Spark[] = [
  { left: 50, top: 6, size: 5, dur: 15, delay: 0, tone: 'nova', metallic: true },
  { left: 46, top: 18, size: 6, dur: 13, delay: 0.6, tone: 'bright' },
  { left: 63, top: 16, size: 4, dur: 15, delay: 3.1, tone: 'bright', metallic: true },
  { left: 37, top: 21, size: 4, dur: 14, delay: 1.8, tone: 'normal' },
  { left: 68, top: 22, size: 5, dur: 12, delay: 0.9, tone: 'bright', metallic: true },
  { left: 12, top: 16, size: 6, dur: 13, delay: 0.3, tone: 'bright', metallic: true },
  { left: 84, top: 12, size: 4, dur: 15, delay: 1.6, tone: 'normal' },
  { left: 90, top: 40, size: 5, dur: 12, delay: 3.4, tone: 'bright', metallic: true },
  { left: 33, top: 46, size: 5, dur: 13, delay: 0.5, tone: 'bright' },
  { left: 16, top: 62, size: 4, dur: 14, delay: 1.4, tone: 'normal', metallic: true },
  { left: 40, top: 66, size: 6, dur: 12, delay: 0.7, tone: 'nova' },
  { left: 72, top: 88, size: 5, dur: 12, delay: 0.4, tone: 'bright' },
  { left: 10, top: 82, size: 4, dur: 13, delay: 1.9, tone: 'normal', metallic: true },
  { left: 44, top: 80, size: 5, dur: 13, delay: 1.5, tone: 'nova', metallic: true },
  { left: 80, top: 70, size: 4, dur: 14, delay: 3.7, tone: 'normal' },
  { left: 26, top: 74, size: 4, dur: 14, delay: 2.0, tone: 'normal' },
];

/** Deterministic pseudo-random so point positions are stable across renders. */
function makePoints(count: number, seed: number): Spark[] {
  let s = seed >>> 0;
  const rnd = (): number => {
    s = (Math.imul(s, 1103515245) + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
  const pts: Spark[] = [];
  for (let i = 0; i < count; i += 1) {
    const r = rnd();
    const tone: Tone = r < 0.06 ? 'nova' : r < 0.22 ? 'bright' : r < 0.55 ? 'normal' : 'subtle';
    const big = rnd();
    pts.push({
      left: Number((rnd() * 100).toFixed(1)),
      top: Number((rnd() * 100).toFixed(1)),
      size: big < 0.12 ? 2.5 : big < 0.45 ? 1.6 : 1,
      dur: 7 + rnd() * 7,
      delay: rnd() * 6,
      tone,
      point: true,
    });
  }
  return pts;
}

// Many tiny point-stars form the "sky" — always visible, gently twinkling.
const POINTS = makePoints(64, 20240611);

export interface SparklesProps {
  /** Number of animated 4-point sparkles from the curated set. */
  count?: number;
  className?: string;
}

/**
 * A subtle but genuinely star-filled sky. Many tiny point-stars (always
 * visible) plus a smaller set of drifting 4-point sparkles, at several
 * brightness levels including a few very bright near-white (nova) stars. Uses
 * only theme tokens. Movement is slow; honors reduced motion.
 */
export function Sparkles({ count = 16, className }: SparklesProps): JSX.Element {
  const reduced = useReducedMotion();
  const stars = STARS.slice(0, Math.min(count, STARS.length));
  const all = [...POINTS, ...stars];

  return (
    <div className={`sparkles${className ? ` ${className}` : ''}`} aria-hidden="true">
      {all.map((s, i) => (
        <span
          key={i}
          className={`sparkle${s.point ? ' sparkle--point' : ''}${s.metallic ? ' sparkle--metallic' : ''}${
            s.tone === 'nova' ? ' sparkle--nova' : ''
          }${reduced ? ' sparkle--static' : ''}`}
          style={
            {
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              animationDuration: `${s.dur}s`,
              animationDelay: `${s.delay}s`,
              '--peak': PEAK[s.tone],
              '--glow-blur': `${BLUR[s.tone]}px`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
